import React, { forwardRef, Ref } from 'react';
import MuiIconButton, { IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';

import { IconButtonSize, IconButtonOptions } from './icon-button.types';

/**
 * Additional styles for overridden button props
 *
 * @note See MuiIconButton overrides in src/themes for the full styling
 * details used by this component
 */

export type IconButtonProps<C extends React.ElementType = 'button'> = Omit<
  MuiIconButtonProps<C, { component?: C }>,
  'size'
> &
  IconButtonOptions;

const getStyles = (size: IconButtonSize) => {
  if (size !== 'regular' && size !== 'large') return;

  const fontSizeMap = {
    regular: '2rem',
    large: '3.6rem',
  };

  return {
    p: 1.5,
    fontSize: fontSizeMap[size],
  };
};

export const IconButton = forwardRef((props: IconButtonProps, ref: Ref<HTMLButtonElement>) => {
  const { size = 'regular', color = 'default', sx, ...restProps } = props;

  // Convert overridden props back to standard MUI props
  const fixedSize = size === 'regular' || size === 'large' ? undefined : size;

  return (
    <MuiIconButton
      sx={(theme) => ({
        ...getStyles(size),
        ...((typeof sx === 'function' ? sx(theme) : sx) as object),
      })}
      size={fixedSize}
      color={color}
      {...restProps}
      ref={ref}
    />
  );
});
